<script lang="ts" setup>
defineProps({
  isMainSection: {
    default: false,
    type: Boolean,
  },
})

const { t } = useI18n()
const { activeTags, availableTags, loading, onLoadMore, onToggleTag, results, search, total } = await useUseCasesSearch()

// Methods
function onCleanup() {
  search.value = ''
}
</script>

<template>
  <LayoutSection>
    <template #left>
      <BaseHeading
        class="mx-auto max-w-4xl"
        :description="t('description')"
        :has-glow="!isMainSection"
        size="lg"
        :tag="isMainSection ? 'h1' : 'h2'"
        :title="t('title')"
      />
    </template>

    <template #right>
      <div class="flex flex-col">
        <div class="-mt-6 mb-8 flex w-full items-center justify-center sm:-mt-6 md:-mt-8">
          <AnimatedBorder class="size-fit">
            <AnimatedInput
              v-model="search"
              class="mx-auto min-w-[300px] sm:min-w-[420px] md:min-w-[520px]"
              :icon="getIcon('search')"
              :loading="loading"
              :placeholders="[t('searchOurUseCases')]"
              size="xl"
              :ui="{ rounded: 'rounded-full' }"
              @cleanup="onCleanup"
            />
          </AnimatedBorder>
        </div>

        <div class="mb-4 flex flex-wrap justify-between gap-4">
          <div class="flex w-full flex-wrap items-center justify-center gap-2 md:w-auto md:justify-normal">
            <UButton
              v-for="tag in availableTags"
              :key="tag.label"
              class="transition-base"
              color="primary"
              :label="tag.label"
              size="lg"
              :variant="activeTags.includes(tag.tag) ? 'solid' : 'subtle'"
              @click="onToggleTag(tag.tag)"
            />
          </div>

          <div class="hidden w-auto md:block">
            <ButtonYouTube />
          </div>

          <div class="block w-full md:hidden">
            <ButtonYouTube block />
          </div>
        </div>

        <div class="grid grid-cols-1 gap-6">
          <UseCaseCard
            v-for="item in results"
            :key="item._path"
            :authors="item.authors"
            :description="item?.matches?.description || item.description"
            :reading-time="item.readingTime.text"
            :schema="item.schema"
            :slug="item._path?.split('/').pop()"
            :tags="item.tags"
            :title="item?.matches?.title || item.title"
            :trophies="item.trophies"
            :youtube-id="item.youtubeId"
          />

          <div
            v-if="!search && total > results.length"
            class="flex items-center justify-center"
          >
            <UButton
              :label="t('loadMoreUseCases')"
              size="xl"
              variant="subtle"
              @click="onLoadMore"
            />
          </div>
        </div>
      </div>
    </template>
  </LayoutSection>
</template>

<i18n lang="json">
{
  "en": {
    "description": "Explore a wide range of applications to improve your marketing, your sales, your customer support, and your user experience.",
    "loadMoreUseCases": "Load more use cases",
    "searchOurUseCases": "Search our use cases",
    "title": "Learn how to use our API by browsing our use cases"
  },
  "es": {
    "description": "Explora una amplia gama de aplicaciones para mejorar tu marketing, ventas, soporte al cliente y experiencia de usuario.",
    "loadMoreUseCases": "Mostrar más casos de uso",
    "searchOurUseCases": "Buscar casos de uso",
    "title": "Descubre cómo usar nuestra API explorando nuestros casos de uso"
  },
  "fr": {
    "description": "Explorez une vaste gamme d'applications pour améliorer votre marketing, votre vente, votre support client et votre expérience utilisateur.",
    "loadMoreUseCases": "Afficher plus de cas d'utilisation",
    "searchOurUseCases": "Rechercher nos cas d'utilisation",
    "title": "Découvrez comment utiliser notre API en parcourant nos cas d'utilisation"
  }
}
</i18n>
